// @ts-check
import { gql } from '@apollo/client';
import { View } from '@react-pdf/renderer';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import React from 'react';
import { Page, Text, Title } from '../../../pdf-design-system';
import { RoomProvider } from '../useRoom';
import StaticCatalogPageContent from './StaticCatalogPageContent';
// import {  Image } from '@react-pdf/renderer';
// @ts-ignore images aren't supported yet.
// import indexStencil from '../Stencil/exhibitor-index.png';
import { findRoomByStandName } from '../helpers/rooms';
import { getNumber, getRoomName } from '../helpers/stands';
import RoomCover from './RoomCover';
import VieVinumPageFrame from './VieVinumPageFrame';
import theme from './vievinumTheme';

/**
 *
 * @param {{
 * data: import('./@types/VieVinumExhibitorIndex').VieVinumExhibitorIndexFragment;
 * bleed: number;
 * [x: string]: any;
 * }} props
 * @returns
 */
export default function VieVinumExhibitorIndex({ data, bleed, ...rest }) {
  const byLetter = groupByLetter(afterQuery(data?.sections, rest));
  return (
    <RoomProvider
      value={{
        name: 'Register',
        floor: { name: 'none', slug: 'none' },
        colors: { primary: 'rgb(84, 86, 86)' },
      }}
    >
      <VieVinumPageFrame bleed={bleed}>
        <RoomCover />
      </VieVinumPageFrame>
      <Page
        size={{ width: `${162 + 2 * bleed}mm`, height: `${234 + 2 * bleed}mm` }}
        style={{
          paddingLeft: `${bleed + 9}mm`,
          paddingTop: `${bleed + 13}mm`,
          paddingRight: `${bleed + 15}mm`,
          paddingBottom: `${bleed + 13}mm`,
        }}
        theme={{
          ...theme,
          components: {
            ...theme.components,
            Text: {
              ...theme.components.Text,
              default: { fontSize: 8, lineHeight: 1.5 },
            },
            Title: {
              ...theme.components.Title,
              default: { fontSize: 12, lineHeight: 1.2 },
            },
          },
        }}
      >
        {/* <Stencil /> */}
        {byLetter.map(({ letter, stands }) => (
          <VieVinumLetterSection key={letter} letter={letter} stands={stands} />
        ))}
      </Page>
      <RoomProvider
        value={{
          name: 'Register',
          floor: { name: 'none', slug: 'none' },
          colors: { primary: 'rgb(84, 86, 86)' },
        }}
      >
        <VieVinumPageFrame bleed={bleed}>
          <StaticCatalogPageContent />
        </VieVinumPageFrame>
        <VieVinumPageFrame bleed={bleed}>
          <StaticCatalogPageContent />
        </VieVinumPageFrame>
      </RoomProvider>
      <RoomProvider
        value={{
          name: 'Notizen',
          floor: { name: 'none', slug: 'none' },
          colors: { primary: 'rgb(84, 86, 86)' },
        }}
      >
        <VieVinumPageFrame bleed={bleed}>
          <StaticCatalogPageContent />
        </VieVinumPageFrame>
        <VieVinumPageFrame bleed={bleed}>
          <StaticCatalogPageContent />
        </VieVinumPageFrame>
      </RoomProvider>
    </RoomProvider>
  );
}

// function Stencil() {
//   return (
//     <Image
//       src={indexStencil}
//       fixed
//       style={{
//         position: 'absolute',
//         top: '-5mm',
//         left: '-5mm',
//         bottom: '-5mm',
//         right: '-5mm',
//         opacity: 0.3,
//       }}
//     />
//   );
// }

/**
 *
 * @param {{
 * letter: string;
 * stands: {entry: string, stand:Stand}[];
 * }} props
 * @returns
 */
function VieVinumLetterSection({ letter, stands }) {
  return (
    <View>
      <Title style={{ marginTop: '3mm' }} minPresenceAhead={150}>
        {letter}
      </Title>
      {stands.map(({ entry, stand: { registration, room, stand } }) => (
        <View
          key={registration.registrationId}
          style={{ flexDirection: 'row' }}
        >
          <Text style={{ width: '45mm' }}>{entry}</Text>
          <Text style={{ width: '57mm' }}>{registration?.profile?.name}</Text>
          {/* Instead of using the room name,
              we need to extract the room from the stand name because of Festsaal & Orchestergang. */}
          <Text style={{ width: '25mm' }}>{getRoomName(stand.name)}</Text>
          <Text style={{ textAlign: 'right', flexGrow: 1 }}>
            {stand.number}
          </Text>
        </View>
      ))}
    </View>
  );
}

/** @type  {import('@apollo/client').TypedDocumentNode<import('./@types/VieVinumExhibitorIndex').VieVinumExhibitorIndexFragment>}*/
VieVinumExhibitorIndex.fragment = gql`
  fragment VieVinumExhibitorIndex on CatalogConfiguration {
    sections {
      ... on RegistrationCatalogSection {
        __typename
        registration {
          registrationId
          companyId
          stand {
            name
          }
          indexEntries: customFieldValue(key: "indexEntries") {
            __typename
            ... on TextFieldValue {
              value
            }
          }
          profile {
            sortName
            name
          }
        }
      }
    }
  }
`;

/** @typedef {ReturnType<afterQuery>[number]} Stand */

/**
 * @param {import('./@types/VieVinumExhibitorIndex').VieVinumExhibitorIndexFragment["sections"]} sections
 * @param {*} rest
 */
// @ts-ignore
function afterQuery(sections, rest) {
  /** @type {Extract<sections[number],{__typename: "RegistrationCatalogSection"}>[]} */
  // @ts-ignore type narrowing in .filter() isn't inferred correctly
  const registrationSections = sections.filter(
    (section) => section.__typename === 'RegistrationCatalogSection'
  );
  return registrationSections.map((section) => ({
    ...section,
    room: findRoomByStandName(section.registration?.stand?.name),
    stand: {
      ...section.registration.stand,
      number: getNumber(section.registration.stand?.name),
    },
  }));
}

/**
 * @param {Stand[]} stands
 */
function groupByLetter(stands) {
  const byName = sortBy(
    stands.flatMap((stand) => {
      const { indexEntries } = stand.registration;
      const entries =
        indexEntries?.__typename === 'TextFieldValue' &&
        indexEntries.value
          ?.split('\n')
          .map((entry) => entry.trim())
          .filter((entry) => Boolean(entry));
      const finalEntries =
        entries?.length > 0
          ? entries
          : [stand.registration.profile.sortName?.trim()];
      return finalEntries.map((entry) => ({
        stand,
        entry,
        sortableEntry: entry
          .trim()
          .toLowerCase()
          .replace(/ö/g, 'o')
          .replace(/ä/g, 'a')
          .replace(/ü/g, 'u')
          .replace(/ß/g, 'ss'),
      }));
    }),
    'sortableEntry'
  );
  const byLetter = Object.entries(
    groupBy(byName, ({ stand, sortableEntry }) => {
      const letter = sortableEntry.charAt(0).toUpperCase();
      switch (letter) {
        case 'Ä':
          return 'A';
        case 'Ö':
          return 'O';
        case 'Ü':
          return 'U';
        default:
          return letter;
      }
    })
  ).map(([letter, stands]) => ({
    letter,
    stands,
  }));
  return byLetter;
}
